import { ClipType, defaultJwtExpiry, getClippingUrlByChannelId, signJwtV2 } from "@core-services/utils"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "@vindral/components"
import { format, isValid } from "date-fns"
import { useCallback, useMemo } from "react"
import { StatusBlock } from "../../../molecules/StatusBlock"
import { RecordingsPlayer } from "../../../organisms/RecordingsPlayer"
import { RouterOutputs } from "../../../trpc"
import { ChannelFindById } from "../ChannelEdit"

type RecordingData = RouterOutputs["clipping"]["getRecordingData"]

const formatDate = (date: Date | undefined) => (date && isValid(date) ? format(date, "yyyy-MM-dd HH:mm") : "")
interface ChannelRecordingsProps {
  channel: ChannelFindById
  recordingData?: RecordingData
  from?: string
  to?: string
}

function NoData() {
  return (
    <div className="mt-4 flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">No recordings available</h1>
      <p>There are currently no recordings available for this channel.</p>
    </div>
  )
}

export function ChannelRecordings({ channel, recordingData, from, to }: ChannelRecordingsProps) {
  const navigate = useNavigate()

  const availableDateRange = useMemo(() => {
    const firstClip = recordingData?.recordedClips[0]
    const lastClip = recordingData?.recordedClips[recordingData.recordedClips.length - 1]
    if (!firstClip || !lastClip) {
      return undefined
    }

    const minDate = new Date(firstClip.start)
    const maxDate = new Date(lastClip.end)
    return { minDate, maxDate }
  }, [recordingData?.recordedClips])

  const disabledDates = useMemo(() => {
    if (!availableDateRange) return undefined
    return { before: availableDateRange.minDate, after: availableDateRange.maxDate }
  }, [availableDateRange])

  const onDateRangeChange = useCallback(
    (dateRange: { from?: string; to?: string } | undefined) => {
      void navigate({
        search: (prev) => {
          const newSearch = { ...prev }
          if (dateRange) {
            const fromParam = dateRange?.from && isValid(new Date(dateRange.from)) ? dateRange.from : undefined
            const toParam = dateRange?.to && isValid(new Date(dateRange.to)) ? dateRange.to : undefined
            const channelRecordingsView = { from: fromParam, to: toParam }
            newSearch.channelRecordingsView = channelRecordingsView
          } else {
            delete newSearch.channelRecordingsView
          }
          return newSearch
        },
        replace: true,
      })
    },
    [navigate]
  )

  if (!recordingData || recordingData.recordedClips.length === 0 || !availableDateRange) {
    return <NoData />
  }

  const m3u8Url = getPlaylistUrl(channel, "m3u8", recordingData.edgeUrl, from, to)
  const mp4Url = getPlaylistUrl(channel, "mp4", recordingData.edgeUrl, from, to)

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-8 flex flex-col gap-2">
        <h1 className="mb-1 font-semibold">Available recordings</h1>
        <div className="flex gap-8">
          <StatusBlock title="From">
            <span data-pw="streamKeyId" className="block max-w-full truncate font-mono text-sm max-[1280px]:max-w-xs">
              {formatDate(availableDateRange.minDate)}
            </span>
          </StatusBlock>
          <StatusBlock title="To">
            <span data-pw="streamKeyId" className="block max-w-full truncate font-mono text-sm max-[1280px]:max-w-xs">
              {formatDate(availableDateRange.maxDate)}
            </span>
          </StatusBlock>
          <StatusBlock title="Total clips">
            <span data-pw="streamKeyId" className="block max-w-full truncate font-mono text-sm max-[1280px]:max-w-xs">
              {recordingData?.recordedClips.length}
            </span>
          </StatusBlock>
        </div>
      </div>
      <RecordingsPlayer
        edgeUrl={recordingData.edgeUrl}
        channelId={channel.channelId}
        from={from}
        to={to}
        disabledDates={disabledDates}
        onDateRangeChange={onDateRangeChange}
        authSecret={channel.organization.authSecret}
      />
      <div>
        <h2 className="mb-2 font-semibold">Download recording for selected period</h2>
        <div className="flex gap-2">
          <a href={mp4Url} target="_blank">
            <Button disabled={!mp4Url}>Mp4</Button>
          </a>
          <a href={m3u8Url} target="_blank">
            <Button disabled={!m3u8Url}>
              Playlist (<span className="italic">.m3u8</span>)
            </Button>
          </a>
        </div>
      </div>
    </div>
  )
}

function getPlaylistUrl(
  channel: ChannelFindById,
  type: ClipType,
  edgeUrl?: string,
  from?: string,
  to?: string
): string | undefined {
  if (!from || !to || !edgeUrl) {
    return
  }

  return getClippingUrlByChannelId({
    edgeUrl,
    channelId: channel.channelId,
    from: new Date(from),
    to: new Date(to),
    authToken: signJwtV2({
      authSecret: channel.organization.authSecret,
      channelId: channel.channelId,
      scope: {
        clipping: {
          from: new Date(from),
          to: new Date(to),
        },
      },
      expiresAt: defaultJwtExpiry(),
    }),
    type,
    download: type === "mp4",
  })
}
